import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Fade } from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { CheckCircleIcon } from '@icons';

import { useTutorial } from '../TutorialProvider';
import { TUTORIAL_REGISTRY } from '../registry';
import { useBottomPanelStyles } from '../sharedStyles';
import { useStyles } from './styles';

const CHECK_DELAY_MS = 1000;
const DISMISS_DELAY_MS = CHECK_DELAY_MS + 2000;

/**
 * Shows a "tutorial completed" notice in the same glass panel and screen
 * position as `TutorialStepText`, naming the tutorial that just finished. An
 * unchecked white circle on the right switches to the same green check used
 * in `TutorialPromptModal` after 1s, and the whole notice fades out 2s after
 * that (3s total). Fades in on appearance and renders nothing the rest of the
 * time.
 */
const TutorialCompletionNotice = () => {
  const { t: translate } = useTranslation();
  const panelClasses = useBottomPanelStyles();
  const classes = useStyles();
  const { completedNoticeTutorialId, dismissCompletionNotice } = useTutorial();

  const [isChecked, setIsChecked] = useState(false);
  // Held separately from completedNoticeTutorialId so the fade-out
  // transition still has a tutorial name to render after it's cleared.
  const [displayedTutorialId, setDisplayedTutorialId] =
    useState<typeof completedNoticeTutorialId>(null);

  useEffect(() => {
    if (!completedNoticeTutorialId) {
      return;
    }

    setDisplayedTutorialId(completedNoticeTutorialId);
    setIsChecked(false);

    const checkTimer = setTimeout(() => setIsChecked(true), CHECK_DELAY_MS);
    const dismissTimer = setTimeout(
      () => dismissCompletionNotice(),
      DISMISS_DELAY_MS,
    );

    return () => {
      clearTimeout(checkTimer);
      clearTimeout(dismissTimer);
    };
  }, [completedNoticeTutorialId, dismissCompletionNotice]);

  if (!displayedTutorialId) {
    return null;
  }

  return (
    <Fade in={!!completedNoticeTutorialId} timeout={300} unmountOnExit>
      <Box sx={panelClasses.root} data-testid="tutorial-completion-notice">
        <Box sx={{ ...panelClasses.panel, ...classes.content }}>
          <Box sx={classes.title}>
            {translate(TUTORIAL_REGISTRY[displayedTutorialId].titleKey)}
          </Box>
          {isChecked ? (
            <CheckCircleIcon />
          ) : (
            <RadioButtonUncheckedIcon sx={classes.uncheckedIcon} />
          )}
        </Box>
      </Box>
    </Fade>
  );
};

export default TutorialCompletionNotice;
